import React, { useEffect, useState } from "react";

import type { ValidateErrorEntity } from "rc-field-form/lib/interface";

import { signupUser } from "services";

import { getLocalKlimVar, useAppType, useGetFarmerType, useGetReferrerByToken, useSignupAttribution, useToggleNavs, useTranslation } from "utils";

import { Alert, ErrorBoundary, Flex, Form, IllustrationCard, Input, Link, MainPage, ScreenSplit, Svg, Typography, Message as message } from "components";

import { IntroScreen, IntroScreenNativeMobile } from "pages/app/account/components/IntroScreen.tsx";
import { SignupButtons } from "pages/app/account/components/SignupButtons.tsx";
import { SignupCheckboxValues, SignupCheckboxes, useSignupCheckboxesAccepted } from "pages/app/account/components/SignupCheckboxes.tsx";

interface SignupFormValues extends SignupCheckboxValues {
  email: string;
}

interface ShareData {
  title: string;
  summary: string;
  link: string;
  image: string;
}

export const SignUp: React.FC = () => {
  const { t } = useTranslation(["signinup", "meta"]);

  const [loading, setLoading] = useState<boolean>(false);
  const [completed, setCompleted] = useState<boolean>(false);
  const [submittedValues, setSubmittedValues] = useState<SignupFormValues | null>(null);
  const [form] = Form.useForm<SignupFormValues>();
  const checkboxesAccepted = useSignupCheckboxesAccepted(form);
  const appType = useAppType();
  const [showIntroPage, setShowIntroPage] = useState<boolean>(appType.app);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const partnerParam = searchParams.get("partner");

    if (partnerParam) {
      // Store it in localStorage so checkReferrer can find it
      const klimSession = JSON.parse(localStorage.getItem("klimSession") || "{}");
      klimSession.partner = partnerParam;
      localStorage.setItem("klimSession", JSON.stringify(klimSession));
    }
  }, []);

  useToggleNavs(false, appType.mobile);

  const farmerType = useGetFarmerType();

  const [share] = useState<ShareData>({
    title: t("meta:signUp.title"),
    summary: t("meta:signUp.description"),
    link: `${process.env.REACT_APP_FARM_URL}/sign-up`,
    image: `${process.env.REACT_APP_FARM_URL}/images/misc/share.png`,
  });

  const userToken: string | undefined = getLocalKlimVar("referrer");
  const partnerToken: string | undefined = getLocalKlimVar("partner");
  const referralToken: string | undefined = partnerToken ?? userToken ?? undefined;
  const referrerQuery = useGetReferrerByToken(referralToken ?? "");
  const attribution = useSignupAttribution();

  const complete = (v: SignupFormValues): void => {
    setLoading(true);
    setSubmittedValues(v);
    const controller = new AbortController();

    signupUser({ ...v, ...attribution, signal: controller.signal })
      .then(() => {
        setCompleted(true);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const incomplete = (v: ValidateErrorEntity<SignupFormValues>): void => {
    if (!v.values.email) {
      message({ content: t("errors.mandatoryFields"), icon: "error" });
      return;
    }
    // REGEX to check email validity
    if (!v.values.email.match(/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/)) {
      message({ content: t("errors.invalidEmail"), icon: "error" });
    }
  };

  const handleLink = (): void => {
    if (!submittedValues) return;

    const controller = new AbortController();

    signupUser({ ...submittedValues, ...attribution, signal: controller.signal }).finally(() => {
      setLoading(false);
    });
  };

  const nativeAppIntro = (
    <Flex vertical align="center" justify="center" gap={32} className="margin-left-100 margin-right-100">
      <IntroScreenNativeMobile farmerType={farmerType} />
      <SignupButtons
        farmerType={farmerType}
        loading={loading}
        onClick={() => {
          setShowIntroPage(false);
        }}
      />
    </Flex>
  );

  const normalIntro = (
    <section className="container">
      <Flex vertical gap={32} className="mini-form">
        <Flex vertical gap={8}>
          <Typography variant="h2">{t("signup.title")}</Typography>
          <Typography variant="paragraph-m">{t("signup.description")}</Typography>
        </Flex>
        <Form form={form} name="signup" onFinish={(v) => complete(v)} onFinishFailed={(v) => incomplete(v)}>
          <Flex vertical gap={32}>
            <Input
              dataTestId="sign-up-input-email"
              name="email"
              label={t("signup.inputs.email")}
              rules={[
                {
                  required: true,
                  type: "email",
                },
              ]}
            />
            <ErrorBoundary>
              <SignupCheckboxes />
            </ErrorBoundary>
            <ErrorBoundary>
              <SignupButtons loading={loading} farmerType={farmerType} submitDisabled={!checkboxesAccepted} />
            </ErrorBoundary>
          </Flex>
        </Form>
      </Flex>
    </section>
  );

  const screenToDisplay = showIntroPage ? nativeAppIntro : normalIntro;

  return (
    <ScreenSplit sideChildren={<IntroScreen farmerType={farmerType} />}>
      <MainPage
        loading={loading}
        pageName={"sign-in-up sign-up"}
        share={share}
        containerClass={appType.mobile ? "small" : ""}
        className={appType.app ? "sign-in-up-container-native-mobile" : "sign-in-up-container"}
      >
        <Flex vertical gap={32}>
          {referrerQuery.data?.name && !completed && (
            <Alert
              dataTestId="alert-banner-signup"
              className="alert-banner"
              description={t("signup.referral.description", {
                referrersName: referrerQuery.data.name,
              })}
              icon=""
              type="green"
              closable={true}
            />
          )}

          {completed && (
            <section className="container">
              <Flex vertical gap={32} className="mini-form">
                <Typography variant="h2">{t("signedUp.title")}</Typography>
                <Flex vertical gap={16}>
                  <IllustrationCard>
                    <Svg svgName="misc/klim_message" />
                  </IllustrationCard>
                  <Typography variant="paragraph-m">{t("signedUp.description")}</Typography>
                  <Link onClick={handleLink}>{t("signedUp.link")}</Link>
                </Flex>
              </Flex>
            </section>
          )}
          {!completed && screenToDisplay}
        </Flex>
      </MainPage>
    </ScreenSplit>
  );
};
